import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  ArrowLeft, Check, Loader2, Package, Star, Crown, XCircle, X, CreditCard, RotateCcw
} from "lucide-react";
import { paymentsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BottomNav } from "../components/BottomNav";
import { DesktopSidebar } from "../components/DesktopSidebar";
import { addBreadcrumb, captureError, captureMessage } from "../lib/sentry";
import { useTheme } from "../context/ThemeContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const isNativeApp = () =>
  typeof window !== "undefined" &&
  (!!window.ReactNativeWebView || window.isNativeApp === true);

const NATIVE_PRODUCT_MAP = {
  pack2: "credits_2",
  pack10: "credits_10",
  subscription: "monthly_25",
};

const postToNative = (payload) => {
  try {
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    }
  } catch {
    // The web checkout should continue normally if the native bridge is absent.
  }
};

/* ---------- inner form inside <Elements> ---------- */
const CheckoutForm = ({ label, clientSecret, onClose, onSuccess, savedMethods = [], savedMethodsLoading = false }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [paymentChoice, setPaymentChoice] = useState("new");

  useEffect(() => {
    if (savedMethods.length > 0) {
      setPaymentChoice(savedMethods[0].id);
    } else {
      setPaymentChoice("new");
    }
  }, [savedMethods]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    const useSavedMethod = paymentChoice !== "new";
    const { error, paymentIntent } = useSavedMethod
      ? await stripe.confirmCardPayment(clientSecret, { payment_method: paymentChoice })
      : await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: "if_required",
        });
    if (error) {
      captureMessage("Stripe payment confirmation failed", "warning", {
        code: error.code,
        type: error.type,
        usedSavedPaymentMethod: useSavedMethod,
      });
      toast.error(error.message || "Payment failed");
      setPaying(false);
    } else {
      addBreadcrumb("Stripe payment confirmed", "payments.checkout", {
        label,
        usedSavedPaymentMethod: useSavedMethod,
      });
      try {
        await onSuccess?.(paymentIntent);
        onClose();
      } catch (verifyError) {
        captureError(verifyError, { action: "verify_confirmed_payment" });
        toast.error(verifyError.response?.data?.detail || "Payment confirmed, but credits could not be verified. Please refresh or contact support.");
        setPaying(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {savedMethodsLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading saved cards...
        </div>
      ) : savedMethods.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Payment method</p>
          {savedMethods.map((method) => (
            <label
              key={method.id}
              className={`flex items-center gap-3 rounded-xl border p-3 text-sm transition-colors ${
                paymentChoice === method.id ? "border-amber-500 bg-amber-500/10" : "border-border bg-card"
              }`}
            >
              <input
                type="radio"
                name="payment-choice"
                value={method.id}
                checked={paymentChoice === method.id}
                onChange={() => setPaymentChoice(method.id)}
                className="accent-amber-500"
              />
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              <span className="flex-1 text-foreground">
                <span className="capitalize">{method.brand}</span> ending in {method.last4}
              </span>
              <span className="text-xs text-muted-foreground">
                {String(method.exp_month).padStart(2, "0")}/{method.exp_year}
              </span>
            </label>
          ))}
          <label
            className={`flex items-center gap-3 rounded-xl border p-3 text-sm transition-colors ${
              paymentChoice === "new" ? "border-amber-500 bg-amber-500/10" : "border-border bg-card"
            }`}
          >
            <input
              type="radio"
              name="payment-choice"
              value="new"
              checked={paymentChoice === "new"}
              onChange={() => setPaymentChoice("new")}
              className="accent-amber-500"
            />
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span className="text-foreground">Use a new card</span>
          </label>
        </div>
      ) : null}

      {paymentChoice === "new" && (
        <PaymentElement options={{ wallets: { applePay: "auto", googlePay: "never", link: "never" } }} />
      )}
      <Button
        type="submit"
        disabled={!stripe || paying}
        className="w-full rounded-xl h-11 bg-amber-500 hover:bg-amber-600 text-white font-medium"
        data-testid="confirm-payment-btn"
      >
        {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {label}
      </Button>
    </form>
  );
};

/* ---------- main page ---------- */
const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const [packages, setPackages] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [subStatus, setSubStatus] = useState("none");
  const [subEndDate, setSubEndDate] = useState(null);
  const [subPlatform, setSubPlatform] = useState("stripe");
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [monthlyRemaining, setMonthlyRemaining] = useState(0);
  const [monthlyUsed, setMonthlyUsed] = useState(0);
  const [monthlyResetDate, setMonthlyResetDate] = useState(null);

  // checkout modal state
  const [checkoutSecret, setCheckoutSecret] = useState(null);
  const [checkoutLabel, setCheckoutLabel] = useState("Pay");
  const [checkoutType, setCheckoutType] = useState("payment");
  const [checkoutKey, setCheckoutKey] = useState(0);
  const [savedMethods, setSavedMethods] = useState([]);
  const [savedMethodsLoading, setSavedMethodsLoading] = useState(false);
  const native = isNativeApp();
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    paymentsApi.getPackages().then(res => {
      setPackages(res.data.packages || []);
      setSubscription(res.data.subscription || null);
      setIsSubscriber(res.data.is_subscriber || false);
      setSubStatus(res.data.subscription_status || "none");
      setSubEndDate(res.data.subscription_end_date || null);
      setSubPlatform(res.data.subscription_platform || "stripe");
      setMonthlyRemaining(res.data.monthly_guides_remaining || 0);
      setMonthlyUsed(res.data.monthly_guides_used || 0);
      setMonthlyResetDate(res.data.monthly_reset_date || null);
    }).catch((error) => {
      captureError(error, { action: "load_payment_packages" });
      toast.error("Failed to load packages");
    })
    .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!native) return;
    const token = localStorage.getItem("token");
    if (token) {
      postToNative({ type: "SET_AUTH_TOKEN", token });
    }
  }, [native]);

  const currencySymbol = { usd: "$", gbp: "\u00a3", eur: "\u20ac" }[user?.default_currency] || "\u20ac";

  // Hide Stripe badge iframes (fixed-position easel overlays)
  const hideStripeBadge = useCallback(() => {
    document.querySelectorAll('iframe[name^="__privateStripeFrame"]').forEach(el => {
      if (el.style.position === 'fixed') el.style.display = 'none';
    });
  }, []);

  // Observe and hide any Stripe badge that appears
  useEffect(() => {
    const observer = new MutationObserver(hideStripeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      hideStripeBadge();
    };
  }, [hideStripeBadge]);

  const openCheckout = (secret, label, type = "payment") => {
    setCheckoutKey(k => k + 1);
    setCheckoutSecret(secret);
    setCheckoutLabel(label);
    setCheckoutType(type);
  };

  const closeCheckout = () => setCheckoutSecret(null);

  useEffect(() => {
    if (!checkoutSecret) return;
    let cancelled = false;
    setSavedMethodsLoading(true);
    paymentsApi.getMethods()
      .then((res) => {
        if (!cancelled) {
          setSavedMethods(res.data.methods || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSavedMethods([]);
          captureError(error, { action: "load_saved_methods_for_checkout", checkoutType });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSavedMethodsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutSecret, checkoutType]);

  const reloadPaymentState = async () => {
    if (refreshUser) await refreshUser();
    const res = await paymentsApi.getPackages();
    setPackages(res.data.packages || []);
    setSubscription(res.data.subscription || null);
    setIsSubscriber(res.data.is_subscriber || false);
    setSubStatus(res.data.subscription_status || "none");
    setSubEndDate(res.data.subscription_end_date || null);
    setSubPlatform(res.data.subscription_platform || "stripe");
    setMonthlyRemaining(res.data.monthly_guides_remaining || 0);
    setMonthlyUsed(res.data.monthly_guides_used || 0);
    setMonthlyResetDate(res.data.monthly_reset_date || null);
  };

  useEffect(() => {
    if (!native) return;

    const handleResult = async (event) => {
      const result = event.detail || {};
      setCheckingOut(null);
      if (result.success) {
        await reloadPaymentState();
        if (result.type === "subscription") {
          toast.success(result.message || "Subscription activated!");
        } else if (result.credits_added) {
          toast.success(`${result.credits_added} credits added!`);
        } else {
          toast.success(result.message || "Purchase successful!");
        }
      } else if (result.error !== "cancelled") {
        captureMessage("Native IAP purchase failed", "warning", { error: result.error });
        toast.error(result.error || "Purchase failed");
      }
    };

    const handleRestoreResult = async (event) => {
      const result = event.detail || {};
      setRestoring(false);
      if (result.status === "ok") {
        await reloadPaymentState();
        toast.success(result.message || `Restored ${result.restored || 0} purchase(s)`);
      } else {
        captureMessage("Native IAP restore failed", "warning", { status: result.status });
        toast.error(result.message || "Restore failed");
      }
    };

    window.addEventListener("iap-result", handleResult);
    window.addEventListener("iap-restore-result", handleRestoreResult);
    return () => {
      window.removeEventListener("iap-result", handleResult);
      window.removeEventListener("iap-restore-result", handleRestoreResult);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native]);

  const onPaymentSuccess = async (paymentIntent) => {
    if (checkoutType === "payment") {
      if (!paymentIntent?.id) {
        throw new Error("Stripe did not return a payment intent to verify");
      }
      const verification = await paymentsApi.verifyPaymentIntent({ payment_intent_id: paymentIntent.id });
      if (!verification.data.verified) {
        throw new Error(`Payment is ${verification.data.status || "not verified"}`);
      }
      await reloadPaymentState();
      const added = verification.data.credits_added || 0;
      toast.success(added > 0 ? `${added} credits added to your account.` : "Payment verified. Credits already added.");
      return;
    }

    await reloadPaymentState();
    toast.success("Payment successful!");
  };

  const handleCreditCheckout = async (pkg) => {
    setCheckingOut(pkg.id);
    addBreadcrumb("Credit checkout requested", "payments.checkout", { packageId: pkg.id });

    if (native) {
      const productId = NATIVE_PRODUCT_MAP[pkg.id];
      if (!productId) {
        captureMessage("Native IAP product mapping missing", "warning", { packageId: pkg.id });
        toast.error("This package isn't available in the app yet.");
        setCheckingOut(null);
        return;
      }
      postToNative({ type: "IAP_PURCHASE", productId });
      return;
    }

    try {
      const res = await paymentsApi.checkoutCredits({ package_id: pkg.id, return_url: window.location.href });
      if (res.data.client_secret) {
        openCheckout(res.data.client_secret, `Pay ${currencySymbol}${pkg.price.toFixed(2)}`, "payment");
      } else {
        captureMessage("Credit checkout missing client secret", "warning", { packageId: pkg.id });
        toast.error("Failed to create checkout");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.toLowerCase().includes("amount") || detail.toLowerCase().includes("minimum")) {
        toast.error("This amount is below Stripe's minimum charge of $0.50. Please choose a larger bundle.");
      } else {
        if (!err.response || err.response.status >= 500) {
          captureError(err, { action: "credit_checkout", packageId: pkg.id });
        }
        toast.error(detail || "Payment could not be processed. Please check your saved payment details in your profile.");
      }
    } finally {
      setCheckingOut(null);
    }
  };

  const handleSubscriptionCheckout = async () => {
    setCheckingOut("subscription");
    addBreadcrumb("Subscription checkout requested", "payments.checkout", {
      currency: user?.default_currency || "eur",
    });

    if (native) {
      postToNative({ type: "IAP_PURCHASE", productId: NATIVE_PRODUCT_MAP.subscription });
      return;
    }

    try {
      const currency = user?.default_currency || "eur";
      const res = await paymentsApi.checkoutSubscription({ return_url: window.location.href, currency });
      if (res.data.client_secret) {
        openCheckout(res.data.client_secret, `Subscribe \u2014 ${currencySymbol}${subscription.price.toFixed(2)} / month`, "subscription");
      } else {
        captureMessage("Subscription checkout missing client secret", "warning", {
          currency,
        });
        toast.error("Failed to create subscription checkout");
      }
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "subscription_checkout", currency: user?.default_currency || "eur" });
      }
      toast.error(err.response?.data?.detail || "Subscription checkout failed");
    } finally {
      setCheckingOut(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    addBreadcrumb("Subscription cancellation requested", "payments.subscription");
    try {
      const res = await paymentsApi.cancelSubscription();
      toast.success(res.data.message || "Subscription cancelled");
      setSubStatus("cancelled");
      if (refreshUser) refreshUser();
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "cancel_subscription" });
      }
      toast.error(err.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleRestorePurchases = () => {
    if (!native) return;
    setRestoring(true);
    postToNative({ type: "IAP_RESTORE" });
  };

  const endDateFormatted = subEndDate
    ? new Date(subEndDate).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 lg:pl-64">
      <DesktopSidebar active="/pricing" />
      <header className="sticky top-0 bg-background/95 backdrop-blur border-b z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl" data-testid="payment-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Buy Credits</h1>
            <p className="text-sm text-muted-foreground">Current balance: {user?.credits || 0} credits</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Active subscription banner */}
        {isSubscriber && (
          <Card className="border-amber-500/30 bg-amber-500/5" data-testid="subscription-active-banner">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">Monthly Culture Pass — Active</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {monthlyRemaining} of {subscription?.monthly_guide_limit || 25} guides remaining this month
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {subStatus === "cancelled"
                      ? `Access until ${endDateFormatted}. Renewal cancelled.`
                      : monthlyResetDate
                        ? `Resets on ${new Date(monthlyResetDate).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`
                        : `Renews on ${endDateFormatted}`}
                  </p>
                  {monthlyRemaining > 0 && monthlyUsed > 0 && (
                    <p className="text-[11px] text-amber-600 mt-1 font-medium">
                      Use your remaining {monthlyRemaining} guides before the reset — unused guides don't roll over!
                    </p>
                  )}
                  {subStatus === "active" && (
                    subPlatform === "apple" || subPlatform === "google" ? (
                      <p className="text-[11px] text-muted-foreground mt-2 italic">
                        Manage or cancel this subscription from your {subPlatform === "apple" ? "Apple ID" : "Google Play"} account settings.
                      </p>
                    ) : (
                      <Button
                        variant="ghost" size="sm"
                        className="text-xs text-destructive hover:text-destructive mt-2 h-7 px-2"
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        data-testid="cancel-subscription-btn"
                      >
                        {cancelling ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                        Cancel subscription
                      </Button>
                    )
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credit packs */}
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Credit Packs</h2>
          {packages.map(pkg => (
            <Card key={pkg.id} className={`cursor-pointer transition-all hover:border-primary/50 ${pkg.popular ? "border-primary/30 shadow-sm" : ""}`} data-testid={`credit-package-${pkg.id}`}>
              <CardContent className="flex items-center justify-between py-4 px-5">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pkg.popular ? "bg-primary/10" : "bg-muted"}`}>
                    <Package className={`w-5 h-5 ${pkg.popular ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{pkg.name}</span>
                      {pkg.popular && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Popular</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{pkg.credits} audio guide{pkg.credits > 1 ? "s" : ""}</p>
                  </div>
                </div>
                <Button
                  size="sm" variant={pkg.popular ? "default" : "outline"}
                  className="rounded-xl h-9 text-sm"
                  disabled={checkingOut === pkg.id}
                  onClick={() => handleCreditCheckout(pkg)}
                  data-testid={`buy-${pkg.id}-btn`}
                >
                  {checkingOut === pkg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{currencySymbol}{pkg.price.toFixed(2)}</>}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Subscription card */}
        {subscription && !isSubscriber && (
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Monthly Culture Pass</h2>
            <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid="subscription-card">
              <CardHeader className="pb-2 pt-5 px-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-semibold">{subscription.title || "Monthly Culture Pass"}</CardTitle>
                    <CardDescription className="text-xs">{subscription.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <ul className="space-y-1.5 mb-4">
                  <li className="text-xs text-muted-foreground flex items-center gap-2"><Check className="w-3.5 h-3.5 text-amber-500" /> Up to 25 audio guides per month</li>
                  <li className="text-xs text-muted-foreground flex items-center gap-2"><Check className="w-3.5 h-3.5 text-amber-500" /> Guides in any language</li>
                  <li className="text-xs text-muted-foreground flex items-center gap-2"><Check className="w-3.5 h-3.5 text-amber-500" /> Cancel anytime, access until billing date</li>
                  <li className="text-xs text-muted-foreground flex items-center gap-2"><Check className="w-3.5 h-3.5 text-amber-500" /> Allowance resets to 25 each month</li>
                </ul>
                <Button
                  className="w-full rounded-xl h-11 bg-amber-500 hover:bg-amber-600 text-white font-medium"
                  disabled={checkingOut === "subscription"}
                  onClick={handleSubscriptionCheckout}
                  data-testid="buy-subscription-btn"
                >
                  {checkingOut === "subscription" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
                  {currencySymbol}{subscription.price.toFixed(2)} / month
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {native && (
          <div className="pt-2">
            <Button
              variant="outline"
              className="w-full rounded-xl h-10 text-sm"
              onClick={handleRestorePurchases}
              disabled={restoring}
              data-testid="restore-purchases-btn"
            >
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Restore Purchases
            </Button>
            <p className="text-[11px] text-muted-foreground text-center mt-2">
              Purchases are processed by {typeof window !== "undefined" && window.nativePlatform === "ios" ? "Apple" : "Google Play"} and credited to your Culture Ken account.
            </p>
          </div>
        )}
      </main>

      {/* Payment modal */}
      {checkoutSecret && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8"
          data-testid="checkout-overlay"
          onClick={e => { if (e.target === e.currentTarget) closeCheckout(); }}
        >
          <div className="relative w-full max-w-md bg-background rounded-2xl shadow-2xl mx-4">
            <div className="sticky top-0 flex items-center justify-between px-5 py-3 border-b bg-background rounded-t-2xl z-10">
              <h2 className="text-sm font-semibold text-foreground">Complete Payment</h2>
              <button onClick={closeCheckout} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid="close-checkout-btn">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="p-5" data-testid="payment-element-container">
              <Elements
                key={`${checkoutKey}-${theme}`}
                stripe={stripePromise}
                options={{
                  clientSecret: checkoutSecret,
                  appearance: { theme: theme === "dark" ? "night" : "stripe" },
                }}
              >
                <CheckoutForm
                  label={checkoutLabel}
                  clientSecret={checkoutSecret}
                  onClose={closeCheckout}
                  onSuccess={onPaymentSuccess}
                  savedMethods={savedMethods}
                  savedMethodsLoading={savedMethodsLoading}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}
      <BottomNav active="/pricing" />
    </div>
  );
};

export default PaymentPage;
